// html2canvas-pro, not the original html2canvas — the original can't parse the oklch colors
// Tailwind v4's default palette uses, and throws on every export.
import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";

/** The report is laid out 8.5in wide at 96 CSS px per inch, so it fills a letter page edge to edge. */
export const REPORT_WIDTH_PX = 816;

/** Room left above and below the blocks on every page; the banner alone starts at the very top of page one. */
export const PAGE_MARGIN_PT = 36;

/** Each `[data-report-block]` is kept whole on one page — a section heading never parts from its figures. */
export const REPORT_BLOCK_SELECTOR = "[data-report-block]";

const CAPTURE_SCALE = 2;

/**
 * Slices go in as JPEG: as PNG a full report came to ~45 MB, too large to email. The report has no
 * transparency to lose — its root paints the page white.
 */
const JPEG_QUALITY = 0.92;

export interface PlacedBlock {
    /** Zero-based page index. */
    page: number;
    /** Distance from the top of the page, in points. */
    top: number;
    /** Printed height in points: the block's own height, or less when it had to shrink to fit a page. */
    height: number;
}

/**
 * Flows blocks down letter pages in order. A block that would cross the bottom margin starts the
 * next page; one taller than a whole page is shrunk to fit one rather than cut in two.
 *
 * The first block starts at the top edge of page one (it is the full-bleed banner); every other
 * page starts below the margin.
 */
export const paginateBlocks = (
    heights: readonly number[],
    { pageHeight, margin }: { pageHeight: number; margin: number },
): PlacedBlock[] => {
    const bottom = pageHeight - margin;
    let page = 0;
    let cursor = 0;
    return heights.map((natural) => {
        const height = Math.min(natural, pageHeight - margin * 2);
        if (cursor + height > bottom && cursor > margin) {
            page += 1;
            cursor = margin;
        }
        const placed = { page, top: cursor, height };
        cursor += height;
        return placed;
    });
};

/**
 * Captures the rendered report once and lays each of its blocks onto letter pages.
 *
 * One capture, then a crop per block: html2canvas clones the whole document on every call, so
 * capturing block by block would cost a clone per section.
 */
export const exportReportPdf = async (root: HTMLElement, filename: string): Promise<void> => {
    // The headings use a web font; a capture taken before it loads prints the fallback. Images need
    // no such wait: html2canvas loads the logo itself while it renders.
    await document.fonts?.ready;

    const rootRect = root.getBoundingClientRect();
    const blocks = Array.from(root.querySelectorAll<HTMLElement>(REPORT_BLOCK_SELECTOR)).map((node) => {
        const rect = node.getBoundingClientRect();
        return { top: rect.top - rootRect.top, height: rect.height };
    });

    const canvas = await html2canvas(root, { scale: CAPTURE_SCALE, backgroundColor: null });

    const pdf = new jsPDF({ unit: "pt", format: "letter" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const ptPerPx = pageWidth / REPORT_WIDTH_PX;

    const placements = paginateBlocks(
        blocks.map((block) => block.height * ptPerPx),
        { pageHeight, margin: PAGE_MARGIN_PT },
    );

    let currentPage = 0;
    placements.forEach((placed, index) => {
        const block = blocks[index];
        if (!block) return;
        while (currentPage < placed.page) {
            pdf.addPage();
            currentPage += 1;
        }

        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = Math.max(1, Math.round(block.height * CAPTURE_SCALE));
        const ctx = slice.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(
            canvas,
            0,
            Math.round(block.top * CAPTURE_SCALE),
            canvas.width,
            slice.height,
            0,
            0,
            canvas.width,
            slice.height,
        );

        // A block shrunk to fit a page keeps its proportions and stays centred.
        const shrink = placed.height / (block.height * ptPerPx);
        const width = pageWidth * shrink;
        pdf.addImage(
            slice.toDataURL("image/jpeg", JPEG_QUALITY),
            "JPEG",
            (pageWidth - width) / 2,
            placed.top,
            width,
            placed.height,
        );
    });

    pdf.save(filename);
};
